/**
 * Mahjong rendering + layout constants.
 *
 * Tile faces are portrait-oriented (taller than wide, unlike domino's 2:1
 * dominoes). The tile BODY (rounded rect, gradient, border, drop shadow) is
 * drawn procedurally; the face ART is a composited sprite (`tileSprites.ts`,
 * `assets/tiles/*.svg`) plus a procedural red corner numeral overlay. Interior
 * features are sized against `TILE_BASE_HEIGHT` so `TileRenderer` can inflate
 * a rack tile by passing a larger rect without every callsite threading a
 * scale factor.
 *
 * Layout geometry is computed directly in viewport pixels (sudoku's approach)
 * rather than against a fixed internal canvas — the table is a centered square
 * with four seat zones + a central discard pond, and (on desktop) a reserved
 * safe area for the future DOM coach panel.
 */

// --- app metadata ---
/** Shown in the main-menu footer + About screen. Mirrors package.json. */
export const APP_VERSION = '0.1.0';

// --- tile face geometry (canvas px) ---
/**
 * How big a tile is drawn, as a multiple of the reference geometry below.
 * **The one knob** — `TileRenderer.tileScale` measures every interior feature
 * (corner radius, border, face-art inset, corner numeral, tile-back inset)
 * against `TILE_BASE_HEIGHT`, so raising this inflates the whole tile in
 * proportion rather than leaving a bigger body wearing the same small
 * furniture. The seat bands and meld strip below scale off it too, since a
 * rack that no longer fits its band is the first thing to break.
 */
export const TILE_SCALE = 1.25;

/** Reference geometry, at `TILE_SCALE` 1. Not drawn at these sizes — see above. */
const TILE_REFERENCE_WIDTH = 44;
/** Also the divisor interior-feature scaling is measured against, which is why
 *  it stays the UNSCALED height rather than tracking `TILE_HEIGHT`. */
export const TILE_BASE_HEIGHT = 60;

export const TILE_WIDTH = TILE_REFERENCE_WIDTH * TILE_SCALE;
export const TILE_HEIGHT = TILE_BASE_HEIGHT * TILE_SCALE;
export const TILE_CORNER_RADIUS = 6;
export const TILE_BORDER_WIDTH = 1;
/** Inner padding the face-art sprite AND the corner numeral both respect. */
export const TILE_INNER_PAD = 6;

// --- typography ---
export const NUMERAL_FONT_PX = 16;
/** Small corner E/S/W/N letter shown only on wind tiles. */
export const WIND_LETTER_FONT_PX = 14;
export const NUMERAL_FONT_STACK = "'Inter','Helvetica Neue',Arial,sans-serif";

// --- tile back ---
export const TILE_BACK_INSET = 4;
export const TILE_BACK_INNER_RADIUS = 3;

// --- shadows, hover, selection ---
export const TILE_SHADOW_COLOR = 'rgba(0, 0, 0, 0.45)';
export const TILE_SHADOW_BLUR = 4;
export const TILE_SHADOW_OFFSET_Y = 2;
export const TILE_HOVER_GLOW_BLUR = 12;
export const SELECTED_RING_WIDTH = 2.5;
export const SELECTED_RING_LIFT = 1.5;
export const HIGHLIGHT_RING_WIDTH = 2;
export const DIM_ALPHA = 0.5;

// --- layout: table + seat zones + pond + coach-safe area ---
/** Outer margin between the table square and the viewport edge. */
export const TABLE_MARGIN_DESKTOP_PX = 32;
export const TABLE_MARGIN_MOBILE_PX = 8;
/** Reserved DOM column (desktop) for the future coach panel (T10). */
export const COACH_PANEL_WIDTH_PX = 320;
/** Seat-zone band thickness — deep enough for a rack of face-up tiles, so it
 *  scales with them (`SEAT_ZONE_MAX_RATIO` still caps it on small viewports). */
export const SEAT_ZONE_THICKNESS_PX = 104 * TILE_SCALE;
/** Cap on seat-zone thickness as a fraction of the table so the pond survives
 *  on small viewports (2 × ratio must stay < 1). */
export const SEAT_ZONE_MAX_RATIO = 0.22;
/** The central pond is split into an N×N grid; each seat owns one edge cell. */
export const POND_GRID = 3;
/**
 * Depth of the BOTTOM/TOP discard cells, as a share of the pond's height —
 * they run deeper than the plain `1/POND_GRID` the other two get.
 *
 * Not symmetry for its own sake: those two cells wrap their rows *downward*, so
 * depth is their scarce axis (a third of the pond height is barely two rows),
 * while left/right wrap sideways across a third of the pond's much greater
 * WIDTH and have depth to spare. Growing these two costs nothing, because they
 * occupy the grid's middle column and the left/right cells occupy the outer
 * ones — the x-ranges are disjoint whatever the depth, so they can never
 * collide.
 *
 * The ceiling is 0.5 (where bottom would meet top), but the real limit is
 * lower: the seat compass sits in the leftover band at the pond's centre and
 * needs roughly `COMPASS_RADIUS_PX` + half a line of type either side of it.
 */
export const POND_SEAT_DEPTH_RATIO = 0.4;

// --- table + compass (T7 renderers) ---
export const TABLE_CORNER_RADIUS = 18;
export const TABLE_BORDER_WIDTH = 3;
export const POND_CORNER_RADIUS = 10;
/** Width of the shadow/highlight band around the pond's sunken-recess bezel. */
export const POND_RECESS_EDGE_PX = 10;
/** Distance from pond centre to each wind letter of the seat compass. */
export const COMPASS_RADIUS_PX = 26;
export const COMPASS_FONT_PX = 15;
/** Half-size of the small diamond marking the compass centre. */
export const COMPASS_CENTER_HALF_PX = 5;

/**
 * The LEFT/RIGHT discard blocks, as shares of the pond. They are the ones whose
 * piles run DOWN the screen, so `HEIGHT` is what decides whether a pile reads as
 * a vertical column or as stubby stacks wrapping sideways: `gridSlots` fits
 * `height / TILE_WIDTH` tiles before starting a new column, and anything under
 * about six of them stops looking vertical at all.
 *
 * That height is deliberately independent of `POND_SEAT_DEPTH_RATIO`. These two
 * blocks stay inside the grid's outer third (`POND_SIDE_INSET_RATIO` is capped
 * to keep them there), which makes them disjoint from bottom/top on the **x**
 * axis — so they may be as tall as they like while those two are as deep as
 * they like, and the two demands never compete.
 */
export const POND_SIDE_WIDTH_RATIO = 0.22;
export const POND_SIDE_HEIGHT_RATIO = 0.7;
/** How far in off the pond's edge those two sit — the width freed by
 *  `POND_SIDE_WIDTH_RATIO` being narrower than the third they live in. */
export const POND_SIDE_INSET_RATIO = 0.12;

/**
 * Clear space kept at the pond's centre for the seat compass, whatever the
 * bottom/top blocks would otherwise take. Sized for the compass's two opposite
 * letters plus their type.
 */
export const POND_CENTRE_CLEARANCE_PX = COMPASS_RADIUS_PX * 2 + COMPASS_FONT_PX;

// --- hand racks + melds + discards (T7 renderers) ---
/** Nominal gap between adjacent tiles in a face-up rack / face-down stack. */
export const HAND_TILE_GAP_PX = 2;
/** Floor on tile-to-tile stride when a hand must overlap to fit its zone. */
export const MIN_TILE_STRIDE_PX = 14;
/** Upward nudge applied to the just-drawn tile in the local rack. */
export const DRAWN_TILE_LIFT_PX = 8 * TILE_SCALE;
/**
 * Open melds render at the SAME size as discards (and as hand tiles). They
 * used to be shrunk to 0.72, which read as "less important" — but a called set
 * is a real, permanent part of a hand, it sits in the pond right next to that
 * seat's discards, and two sizes of face-up tile a few pixels apart mostly made
 * the table look inconsistent. Kept as a named factor rather than deleted so
 * the choice stays visible and reversible in one place.
 */
export const MELD_TILE_SCALE = 1;
export const MELD_TILE_GAP_PX = 1;
/** Inset of the meld group from the trailing (inner-corner) end of a zone. */
export const MELD_END_INSET_PX = 6;
/** Discard-pond tiles render at full hand-tile size — see `DiscardRenderer`. */
export const DISCARD_TILE_GAP_PX = 2;
/** Extent (px) of the meld strip carved from the near-seat edge of a pond
 *  cell — a called set sits here, between that seat's discards and their
 *  rack. One row of full-size meld tiles plus breathing room; it has to lead
 *  `TILE_HEIGHT`, or a called set overflows the strip into the discards. */
export const MELD_STRIP_THICKNESS_PX = TILE_HEIGHT + 10;
/** Clear space between a seat's called sets and its discard pile. Now that both
 *  draw at the same size, an unbroken run of face-up tiles reads as one block —
 *  this is what still says "these four are a called set, those are thrown away". */
export const MELD_STRIP_GAP_PX = 10 * TILE_SCALE;

// --- timings (ms) ---
export const BOT_THINK_DELAY_MS = 650;
export const CLAIM_WINDOW_MS = 4000;
/** Pause after a discard before bot-only claims resolve, so the human sees the
 *  tile land in the pond before play continues. */
export const CLAIM_RESOLVE_DELAY_MS = 450;
export const TILE_DEAL_DURATION_MS = 200;

// --- wall counter (T8 on-canvas readout) ---
export const WALL_COUNT_FONT_PX = 15;
export const WALL_COUNT_PADDING_PX = 12;
/** Space between the invite pill's bottom edge and the readout while the pill shows. */
export const WALL_COUNT_PILL_GAP_PX = 8;

// --- match HUD (T21 on-canvas readout) ---
/** Vertical stride between lines of the multi-line match HUD text block. */
export const MATCH_HUD_LINE_HEIGHT_PX = 18;

// --- reset-view button (on-canvas, top-right of the pond) ---
export const RESET_BUTTON_SIZE_PX = 36;
export const RESET_BUTTON_MARGIN_PX = 12;
export const RESET_BUTTON_RADIUS_PX = 8;

// --- table viewport pan/zoom ---
/** Identity view: the felt renders exactly at its LayoutManager-computed rect. */
export const VIEWPORT_DEFAULT_ZOOM = 1;
export const VIEWPORT_MIN_ZOOM = 0.6;
export const VIEWPORT_MAX_ZOOM = 2.5;
/** Multiplicative zoom applied per wheel tick. */
export const VIEWPORT_WHEEL_ZOOM_FACTOR = 1.08;
